import { lazy, Suspense, type ReactNode } from "react";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { AppLayout } from "./components/AppLayout";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { SessionProvider } from "./session";

const AdminUsersPage = lazy(() =>
  import("./pages/AdminUsersPage").then(({ AdminUsersPage }) => ({ default: AdminUsersPage })),
);
const ClientProjectsPage = lazy(() =>
  import("./pages/ClientProjectsPage").then(({ ClientProjectsPage }) => ({
    default: ClientProjectsPage,
  })),
);
const DashboardPage = lazy(() =>
  import("./pages/DashboardPage").then(({ DashboardPage }) => ({ default: DashboardPage })),
);
const DevelopmentPage = lazy(() =>
  import("./pages/DevelopmentPage").then(({ DevelopmentPage }) => ({
    default: DevelopmentPage,
  })),
);
const EmployeeActivityPage = lazy(() =>
  import("./pages/EmployeeActivityPage").then(({ EmployeeActivityPage }) => ({
    default: EmployeeActivityPage,
  })),
);
const EmployeeProfilePage = lazy(() =>
  import("./pages/EmployeeProfilePage").then(({ EmployeeProfilePage }) => ({
    default: EmployeeProfilePage,
  })),
);
const ForgotPasswordPage = lazy(() =>
  import("./pages/ForgotPasswordPage").then(({ ForgotPasswordPage }) => ({
    default: ForgotPasswordPage,
  })),
);
const HelpPage = lazy(() =>
  import("./pages/HelpPage").then(({ HelpPage }) => ({ default: HelpPage })),
);
const InvitationPage = lazy(() =>
  import("./pages/InvitationPage").then(({ InvitationPage }) => ({ default: InvitationPage })),
);
const LearningAdminPage = lazy(() =>
  import("./pages/LearningAdminPage").then(({ LearningAdminPage }) => ({
    default: LearningAdminPage,
  })),
);
const LearningPage = lazy(() =>
  import("./pages/LearningPage").then(({ LearningPage }) => ({ default: LearningPage })),
);
const LeadsPage = lazy(() =>
  import("./pages/LeadsPage").then(({ LeadsPage }) => ({ default: LeadsPage })),
);
const LoginPage = lazy(() =>
  import("./pages/LoginPage").then(({ LoginPage }) => ({ default: LoginPage })),
);
const MyWorkPage = lazy(() =>
  import("./pages/MyWorkPage").then(({ MyWorkPage }) => ({ default: MyWorkPage })),
);
const NotificationsPage = lazy(() =>
  import("./pages/NotificationsPage").then(({ NotificationsPage }) => ({
    default: NotificationsPage,
  })),
);
const OnboardingAdminPage = lazy(() =>
  import("./pages/OnboardingAdminPage").then(({ OnboardingAdminPage }) => ({
    default: OnboardingAdminPage,
  })),
);
const OrganizationStructurePage = lazy(() =>
  import("./pages/OrganizationStructurePage").then(({ OrganizationStructurePage }) => ({
    default: OrganizationStructurePage,
  })),
);
const PeoplePage = lazy(() =>
  import("./pages/PeoplePage").then(({ PeoplePage }) => ({ default: PeoplePage })),
);
const PoliciesPage = lazy(() =>
  import("./pages/PoliciesPage").then(({ PoliciesPage }) => ({ default: PoliciesPage })),
);
const PayrollPage = lazy(() =>
  import("./pages/PayrollPage").then(({ PayrollPage }) => ({ default: PayrollPage })),
);
const PolicyAdminPage = lazy(() =>
  import("./pages/PolicyAdminPage").then(({ PolicyAdminPage }) => ({ default: PolicyAdminPage })),
);
const PrivacySettingsPage = lazy(() =>
  import("./pages/PrivacySettingsPage").then(({ PrivacySettingsPage }) => ({
    default: PrivacySettingsPage,
  })),
);
const ProfileSettingsPage = lazy(() =>
  import("./pages/ProfileSettingsPage").then(({ ProfileSettingsPage }) => ({
    default: ProfileSettingsPage,
  })),
);
const ProjectConversationsPage = lazy(() =>
  import("./pages/ProjectConversationsPage").then(({ ProjectConversationsPage }) => ({
    default: ProjectConversationsPage,
  })),
);
const ProjectPage = lazy(() =>
  import("./pages/ProjectPage").then(({ ProjectPage }) => ({ default: ProjectPage })),
);
const ProjectsPage = lazy(() =>
  import("./pages/ProjectsPage").then(({ ProjectsPage }) => ({ default: ProjectsPage })),
);
const PublicPrivacyPage = lazy(() =>
  import("./pages/PublicPrivacyPage").then(({ PublicPrivacyPage }) => ({
    default: PublicPrivacyPage,
  })),
);
const ReportsPage = lazy(() =>
  import("./pages/ReportsPage").then(({ ReportsPage }) => ({ default: ReportsPage })),
);
const ResetPasswordPage = lazy(() =>
  import("./pages/ResetPasswordPage").then(({ ResetPasswordPage }) => ({
    default: ResetPasswordPage,
  })),
);
const ReviewsPage = lazy(() =>
  import("./pages/ReviewsPage").then(({ ReviewsPage }) => ({ default: ReviewsPage })),
);
const SecuritySettingsPage = lazy(() =>
  import("./pages/SecuritySettingsPage").then(({ SecuritySettingsPage }) => ({
    default: SecuritySettingsPage,
  })),
);
const SeoWorkspacePage = lazy(() =>
  import("./pages/SeoWorkspacePage").then(({ SeoWorkspacePage }) => ({
    default: SeoWorkspacePage,
  })),
);
const SeoHomePage = lazy(() =>
  import("./pages/SeoHomePage").then(({ SeoHomePage }) => ({ default: SeoHomePage })),
);
const TaskPage = lazy(() =>
  import("./pages/TaskPage").then(({ TaskPage }) => ({ default: TaskPage })),
);
const WelcomePage = lazy(() =>
  import("./pages/WelcomePage").then(({ WelcomePage }) => ({ default: WelcomePage })),
);
const WorkloadPage = lazy(() =>
  import("./pages/WorkloadPage").then(({ WorkloadPage }) => ({ default: WorkloadPage })),
);
const WorkspaceSettingsPage = lazy(() =>
  import("./pages/WorkspaceSettingsPage").then(({ WorkspaceSettingsPage }) => ({
    default: WorkspaceSettingsPage,
  })),
);

function RouteLoading() {
  return (
    <main className="centered-state" aria-busy="true" aria-live="polite">
      <span className="spinner" aria-hidden="true" />
      <p>Loading…</p>
    </main>
  );
}

/** A failed public page or session start shows a Reload message instead of a blank tab. */
function RouteErrorBoundary({ children }: { children: ReactNode }) {
  const location = useLocation();
  return <ErrorBoundary resetKey={location.pathname}>{children}</ErrorBoundary>;
}

export function App() {
  return (
    <BrowserRouter>
      <RouteErrorBoundary>
        <SessionProvider>
          <Suspense fallback={<RouteLoading />}>
            <Routes>
              <Route path="/" element={<WelcomePage />} />
              <Route path="/privacy" element={<PublicPrivacyPage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/invite/:token" element={<InvitationPage />} />
              <Route element={<ProtectedRoute />}>
                <Route element={<AppLayout />}>
                  <Route path="/dashboard" element={<DashboardPage />} />
                  <Route path="/leads" element={<LeadsPage />} />
                  <Route path="/leads/:leadId" element={<LeadsPage />} />
                  <Route path="/my-work" element={<MyWorkPage />} />
                  <Route path="/projects" element={<ProjectsPage />} />
                  <Route path="/seo" element={<SeoHomePage />} />
                  <Route path="/workload" element={<WorkloadPage />} />
                  <Route path="/projects/:projectId" element={<ProjectPage />} />
                  <Route path="/projects/:projectId/seo" element={<SeoWorkspacePage />} />
                  <Route
                    path="/projects/:projectId/conversations"
                    element={<ProjectConversationsPage />}
                  />
                  <Route path="/client-projects" element={<ClientProjectsPage />} />
                  <Route
                    path="/client-projects/:projectId"
                    element={<ProjectConversationsPage />}
                  />
                  <Route path="/tasks/:taskId" element={<TaskPage />} />
                  <Route path="/people" element={<PeoplePage />} />
                  <Route path="/people/:employeeId" element={<EmployeeProfilePage />} />
                  <Route path="/learning" element={<LearningPage />} />
                  <Route path="/learning/admin" element={<LearningAdminPage />} />
                  <Route path="/policies" element={<PoliciesPage />} />
                  <Route path="/payroll" element={<PayrollPage />} />
                  <Route path="/policies/admin" element={<PolicyAdminPage />} />
                  <Route path="/development" element={<DevelopmentPage />} />
                  <Route path="/reports" element={<ReportsPage />} />
                  <Route path="/reports/employee-activity" element={<EmployeeActivityPage />} />
                  <Route path="/reviews" element={<ReviewsPage />} />
                  <Route path="/notifications" element={<NotificationsPage />} />
                  <Route path="/admin/users" element={<AdminUsersPage />} />
                  <Route path="/settings/workspace" element={<WorkspaceSettingsPage />} />
                  <Route path="/settings/security" element={<SecuritySettingsPage />} />
                  <Route path="/settings/profile" element={<ProfileSettingsPage />} />
                  <Route path="/settings/privacy" element={<PrivacySettingsPage />} />
                  <Route path="/settings/structure" element={<OrganizationStructurePage />} />
                  <Route path="/onboarding/templates" element={<OnboardingAdminPage />} />
                  <Route path="/help" element={<HelpPage />} />
                </Route>
              </Route>
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
        </SessionProvider>
      </RouteErrorBoundary>
    </BrowserRouter>
  );
}
